import React from 'react';
import { Condominium, Property } from '../types';
import { translateAmenity } from '../utils/translations';
import { supabase } from '../integrations/supabase/client';
import { 
  X, 
  MapPin, 
  Building, 
  Calendar, 
  Tag,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Home
} from 'lucide-react';

interface CondominiumDetailModalProps {
  condominium: Condominium;
  onClose: () => void;
}

const CondominiumDetailModal: React.FC<CondominiumDetailModalProps> = ({ condominium, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [properties, setProperties] = React.useState<Property[]>([]);
  const [loading, setLoading] = React.useState(true);

  const nextImage = () => {
    if (condominium.images && condominium.images.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === condominium.images!.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (condominium.images && condominium.images.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? condominium.images!.length - 1 : prev - 1
      );
    }
  };

  // Buscar propriedades do condomínio
  React.useEffect(() => {
    const fetchCondominiumProperties = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('properties')
          .select('*')
          .eq('condominium_id', condominium.id)
          .eq('is_active', true)
          .order('title');

        if (error) {
          console.error('Erro ao buscar imóveis do condomínio:', error);
          return;
        }

        // Transformar dados do Supabase para o tipo Property
        const transformedProperties: Property[] = (data || []).map(prop => {
          // Mapear tipo de transação
          let transactionType: 'Venda' | 'Aluguel' | 'Venda/Aluguel';
          switch (prop.transaction_type) {
            case 'sale':
              transactionType = 'Venda';
              break;
            case 'rent':
              transactionType = 'Aluguel';
              break;
            case 'both':
              transactionType = 'Venda/Aluguel';
              break;
            default:
              transactionType = 'Venda';
          }

          return {
            id: prop.id,
            listingId: prop.listing_id,
            propertyCode: prop.listing_id,
            title: prop.title,
            transactionType,
            publicationType: 'STANDARD' as const,
            listDate: prop.created_at,
            lastUpdateDate: prop.updated_at,
            detailViewUrl: `/property/${prop.listing_id}`,
            propertyType: prop.property_type,
            description: prop.description || '',
            listPrice: prop.list_price,
            rentalPrice: prop.rental_price || 0,
            propertyAdministrationFee: prop.condominium_fee || 0,
            yearlyTax: prop.yearly_iptu || 0,
            livingArea: prop.area_built || 0,
            totalArea: prop.total_area || undefined,
            bedrooms: prop.bedrooms || 0,
            suites: prop.suites || 0,
            bathrooms: prop.bathrooms || 0,
            garage: {
              quantity: prop.garage_quantity || 0,
              type: 'Parking Space'
            },
            features: Array.isArray(prop.amenities) ? prop.amenities.filter(f => typeof f === 'string') : [],
            category: (prop.category as any) || 'Médio',
            olx: {
              sendToOlx: true,
              olxType: 'STANDARD'
            },
            location: {
              country: 'Brasil',
              state: prop.state,
              city: prop.city,
              neighborhood: prop.neighborhood,
              address: prop.address,
              streetNumber: prop.street_number || '',
              complement: prop.complement || undefined,
              postalCode: prop.zip_code || '',
              latitude: Number(prop.latitude) || undefined,
              longitude: Number(prop.longitude) || undefined
            },
            media: {
              images: Array.isArray(prop.images) ? prop.images.map((img: any, index: number) => ({
                id: img.id || index.toString(),
                url: img.url || img.preview,
                preview: img.preview || img.url,
                name: img.name || `Imagem ${index + 1}`,
                primary: index === 0
              })) : [],
              youtubeVideoUrl: prop.video_url || undefined,
              virtualTourUrl: prop.virtual_tour_url || undefined
            },
            contact: {
              brokerName: '',
              brokerEmail: '',
              brokerPhone: ''
            },
            owner: {
              name: prop.owner_name,
              phone: prop.owner_phone,
              email: prop.owner_email || ''
            },
            keysLocation: (prop.keys_location as any) || 'Na imobiliária',
            tower: prop.tower || undefined,
            condominiumId: prop.condominium_id || undefined,
            userId: prop.user_id,
            responsibleBrokerId: prop.responsible_broker_id || undefined,
            sendLeadsToQueue: prop.send_leads_to_queue || false,
            createdAt: new Date(prop.created_at),
            isActive: prop.is_active,
            deactivationReason: prop.deactivation_reason || undefined,
            deactivationDate: prop.deactivation_date ? new Date(prop.deactivation_date) : undefined,
            deactivatedBy: prop.deactivated_by || undefined
          };
        });

        setProperties(transformedProperties);
      } catch (error) {
        console.error('Erro ao buscar imóveis do condomínio:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCondominiumProperties();
  }, [condominium.id]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">{condominium.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Images Section */}
          {condominium.images && condominium.images.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <ImageIcon className="w-5 h-5 mr-2 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-800">Imagens</h3>
                <span className="ml-2 text-sm text-gray-500">
                  ({condominium.images.length} foto{condominium.images.length > 1 ? 's' : ''})
                </span>
              </div>
              <div className="relative">
                <img
                  src={condominium.images[currentImageIndex].preview}
                  alt={`${condominium.name} - Imagem ${currentImageIndex + 1}`}
                  className="w-full h-80 object-cover rounded-lg"
                />
                {condominium.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} / {condominium.images.length}
                    </div>
                  </>
                )}
              </div>
              {condominium.images.length > 1 && (
                <div className="flex gap-2 mt-4 overflow-x-auto">
                  {condominium.images.map((image, index) => (
                    <button
                      key={image.id}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                        index === currentImageIndex ? 'border-orange-500' : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={image.preview}
                        alt={`Miniatura ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Address Section */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <MapPin className="w-5 h-5 mr-2 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-800">Endereço</h3>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-800 font-medium">
                {condominium.address.street}, {condominium.address.number}
              </p>
              <p className="text-gray-600">
                {condominium.address.neighborhood} - {condominium.address.city}, {condominium.address.state}
              </p>
              <p className="text-gray-500 text-sm">
                CEP: {condominium.address.postalCode}
              </p>
            </div>
          </div>

          {/* Features Section */}
          {condominium.features && condominium.features.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <Tag className="w-5 h-5 mr-2 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-800">Características</h3>
                <span className="ml-2 text-sm text-gray-500">
                  ({condominium.features.length} item{condominium.features.length > 1 ? 's' : ''})
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {condominium.features.map((feature, index) => (
                  <div
                    key={index}
                    className="bg-orange-50 border border-orange-200 text-orange-800 px-3 py-2 rounded-lg text-sm font-medium"
                  >
                    {translateAmenity(feature)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Properties Section */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <Home className="w-5 h-5 mr-2 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-800">Imóveis Cadastrados</h3>
              <span className="ml-2 text-sm text-gray-500">
                ({properties.length} imóve{properties.length !== 1 ? 'is' : 'l'})
              </span>
            </div>
            
            {loading ? (
              <div className="bg-gray-50 p-8 rounded-lg text-center">
                <p className="text-gray-600">Carregando imóveis...</p>
              </div>
            ) : properties.length > 0 ? (
              <div className="space-y-3">
                {properties.map((property) => (
                  <div key={property.id} className="bg-gray-50 border border-gray-200 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-1">{property.title}</h4>
                        <p className="text-sm text-gray-600 mb-2">
                          {property.location.address} - {property.location.neighborhood}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>Código: {property.listingId}</span>
                          <span>•</span>
                          <span>{property.bedrooms} quartos</span>
                          {property.suites > 0 && (
                            <>
                              <span>•</span>
                              <span>{property.suites} suítes</span>
                            </>
                          )}
                          <span>•</span>
                          <span>{property.garage.quantity} vagas</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="space-y-1">
                          {property.transactionType === 'Venda' && (
                            <div className="text-sm">
                              <span className="text-green-600 font-medium">Venda: </span>
                              <span className="font-semibold">{formatPrice(property.listPrice)}</span>
                            </div>
                          )}
                          {property.transactionType === 'Aluguel' && property.rentalPrice && (
                            <div className="text-sm">
                              <span className="text-blue-600 font-medium">Locação: </span>
                              <span className="font-semibold">{formatPrice(property.rentalPrice)}</span>
                            </div>
                          )}
                          {property.transactionType === 'Venda/Aluguel' && (
                            <>
                              <div className="text-sm">
                                <span className="text-green-600 font-medium">Venda: </span>
                                <span className="font-semibold">{formatPrice(property.listPrice)}</span>
                              </div>
                              {property.rentalPrice && (
                                <div className="text-sm">
                                  <span className="text-blue-600 font-medium">Locação: </span>
                                  <span className="font-semibold">{formatPrice(property.rentalPrice)}</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 p-8 rounded-lg text-center">
                <p className="text-gray-600">Nenhum imóvel cadastrado neste condomínio.</p>
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <Building className="w-5 h-5 mr-2 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-800">Informações</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center text-gray-600 text-sm mb-1">
                  <Calendar className="w-4 h-4 mr-1" />
                  <span>Data de Criação</span>
                </div>
                <p className="text-gray-800 font-medium">
                  {new Date(condominium.createdAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center text-gray-600 text-sm mb-1">
                  <Calendar className="w-4 h-4 mr-1" />
                  <span>Última Atualização</span>
                </div>
                <p className="text-gray-800 font-medium">
                  {new Date(condominium.updatedAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
            <div className="mt-4 bg-gray-50 p-4 rounded-lg">
              <div className="text-gray-600 text-sm mb-1">ID do Condomínio</div>
              <p className="text-gray-800 font-mono text-sm">{condominium.id}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CondominiumDetailModal;